// src/utils/neWazaScoring.js

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const toInt = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : fallback;
};

const toPositiveInt = (value, fallback = 1) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : fallback;
};

const cloneScore = (score = {}) => ({
  ...createEmptyNeWazaScore(),
  ...(score || {}),
});

const TECHNIQUE_FIELD_BY_KIND = {
  // 2 points
  takedown: "takedown2",
  projection: "takedown2",
  amenee: "takedown2",
  ameneeAuSol: "takedown2",
  takedown2: "takedown2",

  kneeOnBelly: "kneeOnBelly2",
  genouVentre: "kneeOnBelly2",
  genouSurEstomac: "kneeOnBelly2",
  ukiGatame: "kneeOnBelly2",
  kneeOnBelly2: "kneeOnBelly2",

  sweep: "sweep2",
  renversement: "sweep2",
  sweep2: "sweep2",

  penaltyBonus: "penaltyBonus2",
  penaltyBonus2: "penaltyBonus2",

  other2: "other2",

  // 3 points
  guardPass: "guardPass3",
  passage: "guardPass3",
  passageDeGarde: "guardPass3",
  guardPass3: "guardPass3",

  other3: "other3",

  // 4 points
  mount: "mount4",
  montee: "mount4",
  monteeAvant: "mount4",
  monteeArriere: "mount4",
  mount4: "mount4",

  backControl: "backControl4",
  dos: "backControl4",
  priseDeDos: "backControl4",
  backControl4: "backControl4",

  other4: "other4",
};

function resolveTechniqueField(kind) {
  const raw = String(kind || "").trim();
  return TECHNIQUE_FIELD_BY_KIND[raw] || null;
}

function getTerminalReason(redScore, blueScore) {
  const redDQ = isDisqualified(redScore);
  const blueDQ = isDisqualified(blueScore);

  if (redScore.forfait && blueScore.forfait) return "double_forfait";
  if (redScore.medicalStop && blueScore.medicalStop) return "double_medical";
  if (redDQ && blueDQ) return "double_disqualification";
  if (redScore.submission && blueScore.submission) return "double_submission";

  if (redScore.forfait) return "forfait_red";
  if (blueScore.forfait) return "forfait_blue";

  if (redScore.medicalStop) return "medical_red";
  if (blueScore.medicalStop) return "medical_blue";

  if (redDQ) return "disqualification_red";
  if (blueDQ) return "disqualification_blue";

  if (redScore.submission) return "submission_red";
  if (blueScore.submission) return "submission_blue";

  return null;
}

function getWinModeFromComparison(redScore, blueScore, isGoldenScore) {
  const redPts = getNeWazaPoints(redScore).total;
  const bluePts = getNeWazaPoints(blueScore).total;

  if (redPts !== bluePts) {
    return isGoldenScore ? "golden_score" : "points";
  }

  const redAdvantages = toInt(redScore.advantages);
  const blueAdvantages = toInt(blueScore.advantages);

  if (redAdvantages !== blueAdvantages) {
    return isGoldenScore ? "golden_score" : "advantages";
  }

  const redPenalties = toInt(redScore.penalties);
  const bluePenalties = toInt(blueScore.penalties);

  if (redPenalties !== bluePenalties) {
    return isGoldenScore ? "golden_score" : "penalties";
  }

  return null;
}

/* -------------------------------------------------------------------------- */
/* Base score                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Score "vierge" pour un combattant Ne-Waza.
 *
 * Notes :
 * - On stocke des COMPTEURS d'actions, pas les points déjà calculés.
 * - Le total est recalculé via getNeWazaPoints().
 * - other2 / other3 / other4 permettent d'avancer même si
 *   toute la nomenclature officielle n'est pas encore intégrée.
 */
export function createEmptyNeWazaScore() {
  return {
    // Actions à 2 points
    takedown2: 0,
    kneeOnBelly2: 0,
    sweep2: 0,
    penaltyBonus2: 0, // bonus de 2 pts donné à l'adversaire sur 3e pénalité
    other2: 0,

    // Actions à 3 points
    guardPass3: 0,
    other3: 0,

    // Actions à 4 points
    mount4: 0,
    backControl4: 0,
    other4: 0,

    // Avantages / pénalités
    advantages: 0,
    penalties: 0,

    // États terminaux
    submission: false,
    disqualified: false, // disqualification directe
    penaltyDisqualification: false, // 4e pénalité
    forfait: false,
    medicalStop: false,
  };
}

/* -------------------------------------------------------------------------- */
/* Lecture du score                                                           */
/* -------------------------------------------------------------------------- */

export function getNeWazaPoints(score = {}) {
  const s = cloneScore(score);

  const takedown2 = toInt(s.takedown2);
  const kneeOnBelly2 = toInt(s.kneeOnBelly2);
  const sweep2 = toInt(s.sweep2);
  const penaltyBonus2 = toInt(s.penaltyBonus2);
  const other2 = toInt(s.other2);

  const guardPass3 = toInt(s.guardPass3);
  const other3 = toInt(s.other3);

  const mount4 = toInt(s.mount4);
  const backControl4 = toInt(s.backControl4);
  const other4 = toInt(s.other4);

  const total2 =
    (takedown2 + kneeOnBelly2 + sweep2 + penaltyBonus2 + other2) * 2;
  const total3 = (guardPass3 + other3) * 3;
  const total4 = (mount4 + backControl4 + other4) * 4;

  return {
    takedown2,
    kneeOnBelly2,
    sweep2,
    penaltyBonus2,
    other2,
    guardPass3,
    other3,
    mount4,
    backControl4,
    other4,
    total2,
    total3,
    total4,
    total: total2 + total3 + total4,
  };
}

export function isDisqualified(score = {}) {
  return Boolean(score?.disqualified || score?.penaltyDisqualification);
}

export function getNeWazaStats(score = {}) {
  const s = cloneScore(score);
  const pts = getNeWazaPoints(s);

  return {
    ...pts,
    advantages: toInt(s.advantages),
    penalties: toInt(s.penalties),
    submission: Boolean(s.submission),
    disqualified: Boolean(s.disqualified),
    penaltyDisqualification: Boolean(s.penaltyDisqualification),
    isDisqualified: isDisqualified(s),
    forfait: Boolean(s.forfait),
    medicalStop: Boolean(s.medicalStop),
  };
}

/* -------------------------------------------------------------------------- */
/* Techniques                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Ajoute une action de score.
 *
 * kind possibles actuellement :
 * - "takedown" / "projection" / "amenee" / "takedown2"
 * - "kneeOnBelly" / "genouVentre" / "ukiGatame" / "kneeOnBelly2"
 * - "sweep" / "renversement" / "sweep2"
 * - "guardPass" / "passage" / "guardPass3"
 * - "mount" / "montee" / "mount4"
 * - "backControl" / "dos" / "backControl4"
 * - "other2" / "other3" / "other4"
 */
export function addTechnique(score, { kind, count = 1 } = {}) {
  const next = cloneScore(score);
  const field = resolveTechniqueField(kind);
  if (!field) return next;

  next[field] = toInt(next[field]) + toPositiveInt(count, 1);
  return next;
}

export function removeTechnique(score, { kind, count = 1 } = {}) {
  const next = cloneScore(score);
  const field = resolveTechniqueField(kind);
  if (!field) return next;

  next[field] = Math.max(0, toInt(next[field]) - toPositiveInt(count, 1));
  return next;
}

/* -------------------------------------------------------------------------- */
/* Avantages                                                                  */
/* -------------------------------------------------------------------------- */

export function addAdvantage(score, count = 1) {
  const next = cloneScore(score);
  next.advantages = toInt(next.advantages) + toPositiveInt(count, 1);
  return next;
}

export function removeAdvantage(score, count = 1) {
  const next = cloneScore(score);
  next.advantages = Math.max(
    0,
    toInt(next.advantages) - toPositiveInt(count, 1),
  );
  return next;
}

/* -------------------------------------------------------------------------- */
/* Pénalités                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Ajoute UNE pénalité au combattant pénalisé.
 *
 * Règles implémentées :
 * 1re pénalité : avertissement
 * 2e pénalité  : 1 avantage à l'adversaire
 * 3e pénalité  : 2 points à l'adversaire
 * 4e pénalité  : disqualification
 *
 * Retour :
 * {
 *   penalized,
 *   opponent,
 *   consequence // "warning" | "advantage_opponent" | "two_points_opponent" | "disqualification"
 * }
 */
export function addPenalty(scorePenalized, scoreOpponent) {
  const penalized = cloneScore(scorePenalized);
  const opponent = cloneScore(scoreOpponent);

  const currentPenalties = toInt(penalized.penalties);

  if (currentPenalties >= 4 || penalized.penaltyDisqualification) {
    return {
      penalized,
      opponent,
      consequence: null,
    };
  }

  const nextPenalties = currentPenalties + 1;
  penalized.penalties = nextPenalties;

  let consequence = "warning";

  if (nextPenalties === 2) {
    opponent.advantages = toInt(opponent.advantages) + 1;
    consequence = "advantage_opponent";
  } else if (nextPenalties === 3) {
    opponent.penaltyBonus2 = toInt(opponent.penaltyBonus2) + 1;
    consequence = "two_points_opponent";
  } else if (nextPenalties >= 4) {
    penalized.penaltyDisqualification = true;
    consequence = "disqualification";
  }

  return {
    penalized,
    opponent,
    consequence,
  };
}

/**
 * Annule la DERNIÈRE pénalité du combattant pénalisé
 * et retire si besoin sa conséquence chez l'adversaire.
 */
export function removePenalty(scorePenalized, scoreOpponent) {
  const penalized = cloneScore(scorePenalized);
  const opponent = cloneScore(scoreOpponent);

  const currentPenalties = toInt(penalized.penalties);

  if (currentPenalties <= 0) {
    return {
      penalized,
      opponent,
      consequence: null,
    };
  }

  let consequence = "warning";

  // On retire d'abord l'effet de la dernière pénalité appliquée
  if (currentPenalties === 2) {
    opponent.advantages = Math.max(0, toInt(opponent.advantages) - 1);
    consequence = "advantage_opponent";
  } else if (currentPenalties === 3) {
    opponent.penaltyBonus2 = Math.max(0, toInt(opponent.penaltyBonus2) - 1);
    consequence = "two_points_opponent";
  } else if (currentPenalties >= 4) {
    consequence = "disqualification";
  }

  penalized.penalties = currentPenalties - 1;
  penalized.penaltyDisqualification = penalized.penalties >= 4;

  return {
    penalized,
    opponent,
    consequence,
  };
}

/* -------------------------------------------------------------------------- */
/* États finaux                                                               */
/* -------------------------------------------------------------------------- */

export function setSubmission(score, value = true) {
  return { ...cloneScore(score), submission: Boolean(value) };
}

export function setDisqualification(score, value = true) {
  return { ...cloneScore(score), disqualified: Boolean(value) };
}

export function setForfait(score, value = true) {
  return { ...cloneScore(score), forfait: Boolean(value) };
}

export function setMedicalStop(score, value = true) {
  return { ...cloneScore(score), medicalStop: Boolean(value) };
}

export function clearTerminalStates(score) {
  const next = cloneScore(score);
  next.submission = false;
  next.disqualified = false;
  next.penaltyDisqualification = false;
  next.forfait = false;
  next.medicalStop = false;
  return next;
}

/* -------------------------------------------------------------------------- */
/* Comparaison                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Compare Rouge vs Bleu.
 *
 * Retour :
 * -  1 => Rouge devant
 * - -1 => Bleu devant
 * -  0 => égalité parfaite
 *
 * Ordre de comparaison :
 * 1. points
 * 2. avantages
 * 3. moins de pénalités
 */
export function compareNeWazaScores(redScore, blueScore) {
  const redPts = getNeWazaPoints(redScore).total;
  const bluePts = getNeWazaPoints(blueScore).total;

  if (redPts > bluePts) return 1;
  if (bluePts > redPts) return -1;

  const redAdvantages = toInt(redScore?.advantages);
  const blueAdvantages = toInt(blueScore?.advantages);

  if (redAdvantages > blueAdvantages) return 1;
  if (blueAdvantages > redAdvantages) return -1;

  const redPenalties = toInt(redScore?.penalties);
  const bluePenalties = toInt(blueScore?.penalties);

  if (redPenalties < bluePenalties) return 1;
  if (bluePenalties < redPenalties) return -1;

  return 0;
}

/* -------------------------------------------------------------------------- */
/* Calcul du résultat                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Calcule le résultat du combat Ne-Waza.
 *
 * options :
 * - isGoldenScore : false par défaut
 *
 * Retour :
 * {
 *   result1, result2,      // "V" / "D" / "N"
 *   points1, points2,      // points exacts
 *   advantages1, advantages2,
 *   penalties1, penalties2,
 *   winnerColor,           // "red" | "blue" | null
 *   needGoldenScore,       // true si égalité parfaite en fin de temps réglementaire
 *   needRefereeDecision,   // sécurité si cas incohérent
 *   details
 * }
 */
export function calculateNeWazaResult(
  redScore,
  blueScore,
  { isGoldenScore = false } = {},
) {
  const red = cloneScore(redScore);
  const blue = cloneScore(blueScore);

  const redStats = getNeWazaStats(red);
  const blueStats = getNeWazaStats(blue);

  let winner = null;
  let mode = null;

  // 1) Cas terminaux immédiats
  const terminalReason = getTerminalReason(red, blue);

  if (terminalReason === "forfait_red") {
    winner = "blue";
    mode = "forfait";
  } else if (terminalReason === "forfait_blue") {
    winner = "red";
    mode = "forfait";
  } else if (terminalReason === "medical_red") {
    winner = "blue";
    mode = "medical";
  } else if (terminalReason === "medical_blue") {
    winner = "red";
    mode = "medical";
  } else if (terminalReason === "disqualification_red") {
    winner = "blue";
    mode = "disqualification";
  } else if (terminalReason === "disqualification_blue") {
    winner = "red";
    mode = "disqualification";
  } else if (terminalReason === "submission_red") {
    winner = "red";
    mode = "submission";
  } else if (terminalReason === "submission_blue") {
    winner = "blue";
    mode = "submission";
  } else if (
    terminalReason === "double_forfait" ||
    terminalReason === "double_medical" ||
    terminalReason === "double_disqualification" ||
    terminalReason === "double_submission"
  ) {
    return {
      result1: "N",
      result2: "N",
      points1: redStats.total,
      points2: blueStats.total,
      advantages1: redStats.advantages,
      advantages2: blueStats.advantages,
      penalties1: redStats.penalties,
      penalties2: blueStats.penalties,
      winnerColor: null,
      needGoldenScore: false,
      needRefereeDecision: true,
      details: {
        reason: terminalReason,
        redScore: red,
        blueScore: blue,
        redStats,
        blueStats,
      },
    };
  }

  // 2) Sinon on compare les scores
  if (!winner) {
    const comparison = compareNeWazaScores(red, blue);

    if (comparison > 0) {
      winner = "red";
      mode = getWinModeFromComparison(red, blue, isGoldenScore);
    } else if (comparison < 0) {
      winner = "blue";
      mode = getWinModeFromComparison(red, blue, isGoldenScore);
    }
  }

  // 3) Égalité parfaite
  if (!winner) {
    if (isGoldenScore) {
      return {
        result1: "N",
        result2: "N",
        points1: redStats.total,
        points2: blueStats.total,
        advantages1: redStats.advantages,
        advantages2: blueStats.advantages,
        penalties1: redStats.penalties,
        penalties2: blueStats.penalties,
        winnerColor: null,
        needGoldenScore: false,
        needRefereeDecision: true,
        details: {
          reason: "golden_score_without_decision",
          redScore: red,
          blueScore: blue,
          redStats,
          blueStats,
        },
      };
    }

    return {
      result1: "N",
      result2: "N",
      points1: redStats.total,
      points2: blueStats.total,
      advantages1: redStats.advantages,
      advantages2: blueStats.advantages,
      penalties1: redStats.penalties,
      penalties2: blueStats.penalties,
      winnerColor: null,
      needGoldenScore: true,
      needRefereeDecision: false,
      details: {
        reason: "perfect_draw",
        redScore: red,
        blueScore: blue,
        redStats,
        blueStats,
      },
    };
  }

  const redIsWinner = winner === "red";

  return {
    result1: redIsWinner ? "V" : "D",
    result2: redIsWinner ? "D" : "V",
    points1: redStats.total,
    points2: blueStats.total,
    advantages1: redStats.advantages,
    advantages2: blueStats.advantages,
    penalties1: redStats.penalties,
    penalties2: blueStats.penalties,
    winnerColor: winner,
    needGoldenScore: false,
    needRefereeDecision: false,
    details: {
      reason: mode,
      isGoldenScore,
      redScore: red,
      blueScore: blue,
      redStats,
      blueStats,
    },
  };
}